import DashboardWidgetLoader from "../../../components/widgetLoader/dashboardWidgetLoader";
import IndependentDashboardWidgetLoader from "../../../components/widgetLoader/independentDashboardWidgetLoader";
import { useMemo } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { MdControlCamera } from "react-icons/md";
import DashboardLoadingGrid from "./dashboardLoader";
import Resizer from "./resizer";
import { useDashboardGridLayout } from "../../../helpers/hooks/useDashboardGridLayout";
import {
  breakpoints,
  columns,
  widgetIdSplitSymbol
} from "../../../helpers/gridLayoutConfigurations";
import { useSelector } from "react-redux";
import { type RootState } from "../../../state/store";

interface DashboardWidgetsGridProps {
  dashboard: string;
  dashboardWidgets: any[];
}

const DashboardWidgetsGrid = ({ dashboard, dashboardWidgets }: DashboardWidgetsGridProps) => {
  const GridLayout = useMemo(() => WidthProvider(Responsive), []);

  // Use staff_id from company details for proper scoping
  const companyDetails = useSelector((state: RootState) => state.company.company_details);
  const staffId = (companyDetails as any)?.user_id || 0;

  const { gridLayoutConfiguration, saveLayoutToLS, shouldLoadGridLayout } = useDashboardGridLayout({
    dashboard,
    dashboardWidgets,
    staffId
  });

  const handleOnDragStop = (layout) => {
    saveLayoutToLS(layout);
  };

  const handleResizeStop = (layout) => {
    saveLayoutToLS(layout);
  };

  return gridLayoutConfiguration ? (
    <GridLayout
      key={JSON.stringify(gridLayoutConfiguration)}
      breakpoints={breakpoints}
      rowHeight={25}
      verticalCompact
      cols={columns}
      layouts={gridLayoutConfiguration || {}}
      containerPadding={{
        xxl: [40, 20],
        xl: [35, 18],
        lg: [30, 15],
        md: [20, 12],
        sm: [15, 10],
        xs: [10, 8],
        xxs: [5, 5]
      }}
      margin={[20, 20]}
      onDragStop={handleOnDragStop}
      onResizeStop={handleResizeStop}
      draggableHandle=".drag-handle"
      resizeHandles={["se"]}
      className="grid-layout"
    >
      {dashboardWidgets.map((widget) => {
        const widgetKey = `${widget.url}${widgetIdSplitSymbol}${widget.widget.type}`;
        const childWidgets = [...(widget.widgets || []), widget.groups].filter(Boolean);

        return (
          <div key={widgetKey}>
            {/* Use IndependentDashboardWidgetLoader for widgets that require separate fetch */}
            {widget.requires_fetch ? (
              <IndependentDashboardWidgetLoader
                url={widget.url}
                type={widget.widget.type}
                childWidgets={childWidgets}
                widgetData={widget.widget}
                fetchUrl={widget.fetch_url}
                isTimelessWidget={widget.is_timeless_widget}
                dashboard={dashboard}
              />
            ) : (
              <DashboardWidgetLoader
                url={widget.url}
                type={widget.widget.type}
                childWidgets={childWidgets}
                widgetData={widget.widget}
                isTimelessWidget={widget.is_timeless_widget}
              />
            )}
            <div className="resizer">
              <Resizer />
            </div>
            <div className="drag-handle">
              <MdControlCamera />
            </div>
          </div>
        );
      })}
    </GridLayout>
  ) : (
    <DashboardLoadingGrid />
  );
};

export { DashboardWidgetsGrid };
